(() => {
  if (customElements.get('ads-landing')) return;

  const ROW_KEYS = ['newest', 'best_sellers', 'dux_best_sellers', 'bux_best_sellers'];
  const token = (value, fallback, limit = 64) => (
    typeof value === 'string' && value.length <= limit && /^[a-zA-Z0-9_-]+$/.test(value)
      ? value : fallback
  );

  class AdsLanding extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.controller = new AbortController();
        this.privacyRequested = false;
        this.seenRows ||= new Set();
        this.mobile = window.matchMedia('(max-width: 749px)');
        const initial = this.dataset.initialCount === '6' ? 6 : 4;
        this.rows = [...this.querySelectorAll('[data-gear-row]')].map((element, index) => {
          const cards = [...element.querySelectorAll('[data-gear-card]')];
          return {
            element, cards, index,
            heading: element.querySelector('[data-gear-row-title]'),
            button: element.querySelector('[data-gear-reveal]'),
            extras: cards.slice(initial),
            expanded: false
          };
        });
        this.jumps = [...this.querySelectorAll('[data-gear-jump]')];
        this.nav = this.querySelector('.ads-landing__nav');
        this.header = document.querySelector('store-header');
        this.videos = [...this.querySelectorAll('[data-gear-video]')].map((element, index) => ({
          element, index,
          video: element.querySelector('video'),
          poster: element.querySelector('.ads-landing__poster'),
          button: element.querySelector('[data-gear-play]'),
          status: element.querySelector('[data-gear-video-status]'),
          request: 0,
          failed: false
        })).filter(({ video, button }) => video && button);

        this.listen(this, 'click', (event) => this.onClick(event));
        this.listen(this, 'auxclick', (event) => {
          if (event.button === 1) this.trackLink(event);
        });
        this.listen(window, 'resize', () => this.scheduleLayout(), { passive: true });
        // The heading observer only fires when a heading crosses its band; a smooth
        // jump that settles inside the band would leave the strip one row behind.
        this.listen(window, 'scroll', () => {
          if (this.jumpFrame) return;
          this.jumpFrame = requestAnimationFrame(() => { this.jumpFrame = 0; this.updateCurrentJump(); });
        }, { passive: true });
        this.listen(document, 'visibilitychange', () => {
          if (document.hidden) this.videos.forEach(({ video }) => video.pause());
          else {
            this.trackExposures();
            this.videos.forEach((record) => this.syncAutoplay(record));
          }
        });
        this.mediaChange = () => {
          this.applyRows();
          this.scheduleLayout();
        };
        if (this.mobile.addEventListener) this.mobile.addEventListener('change', this.mediaChange);
        else this.mobile.addListener(this.mediaChange);

        this.applyRows();
        this.setupRatings();
        this.setupCarousels();
        this.videos.forEach((record) => this.prepareVideo(record));
        this.setupVideoAutoplay();
        this.scheduleLayout();
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
          if (this.header) this.resizeObserver.observe(this.header);
          if (this.nav) this.resizeObserver.observe(this.nav);
        }
        this.setupPrivacy();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.ratingObserver?.disconnect();
      this.videoObserver?.disconnect();
      clearTimeout(this.ratingFallback);
      this.resizeObserver?.disconnect();
      this.headingObserver?.disconnect();
      this.exposureObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = 0;
      cancelAnimationFrame(this.jumpFrame);
      this.jumpFrame = 0;
      if (this.mobile?.removeEventListener) this.mobile.removeEventListener('change', this.mediaChange);
      else this.mobile?.removeListener(this.mediaChange);
      this.videos?.forEach((record) => {
        record.request += 1;
        record.video.pause();
      });
      this.rows?.forEach(({ cards, button }) => {
        cards.forEach((card) => { card.hidden = false; });
        if (button) button.hidden = true;
      });
    }

    listen(target, name, handler, options = {}) {
      target.addEventListener(name, handler, { ...options, signal: this.controller.signal });
    }

    setupRatings() {
      const summaries = [...this.querySelectorAll('[data-ads-review-summary]')];
      if (!summaries.length) return;
      // Markup starts `hidden` so a new product never flashes a "0 reviews" rating
      // while the Yotpo widget is still loading. We only reveal once the widget
      // confirms a nonzero count, and fail open on a timeout in case it never loads.
      this.updateRatings(summaries);
      if ('MutationObserver' in window) {
        this.ratingObserver = new MutationObserver(() => this.updateRatings(summaries));
        summaries.forEach((summary) => this.ratingObserver.observe(summary, {
          childList: true, characterData: true, subtree: true
        }));
      }
      this.ratingFallback = setTimeout(() => {
        summaries.forEach((summary) => {
          if (!summary.querySelector('.yotpo-sr-bottom-line-text--right-panel')) summary.hidden = false;
        });
      }, 6000);
    }

    setupCarousels() {
      this.querySelectorAll('[data-gear-carousel]').forEach((carousel) => {
        const track = carousel.querySelector('[data-gear-carousel-track]');
        const prev = carousel.querySelector('[data-gear-carousel-prev]');
        const next = carousel.querySelector('[data-gear-carousel-next]');
        if (!track || !prev || !next) return;
        const update = () => {
          const max = track.scrollWidth - track.clientWidth;
          prev.hidden = next.hidden = max <= 2;
          prev.disabled = track.scrollLeft <= 2;
          next.disabled = track.scrollLeft >= max - 2;
        };
        const step = (direction) => {
          const item = track.firstElementChild;
          if (!item) return;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          const width = item.getBoundingClientRect().width + gap;
          const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / width));
          track.scrollBy({ left: direction * perPage * width });
        };
        this.listen(prev, 'click', () => step(-1));
        this.listen(next, 'click', () => step(1));
        this.listen(track, 'scroll', update, { passive: true });
        this.listen(window, 'resize', update, { passive: true });
        requestAnimationFrame(update);
      });
    }

    updateRatings(summaries) {
      summaries.forEach((summary) => {
        const count = summary.querySelector('.yotpo-sr-bottom-line-text--right-panel');
        if (!count) return;
        // One or two reviews read as noise beside the buy button; the product page still lists them.
        const total = Number((count.textContent || '').replace(/[^\d]/g, '')) || 0;
        summary.hidden = total < 3;
        if (summary.hidden) return;
        // Yotpo's compact widget shows one star and a count, so surface the average it reports.
        const button = summary.querySelector('.yotpo-sr-bottom-line-button');
        const match = /([\d.]+)\s*out\s*(?:of\s*)?5/i.exec(button?.getAttribute('aria-label') || '');
        if (!match || summary.querySelector('[data-ads-rating-score]')) return;
        const score = document.createElement('span');
        score.className = 'ads-landing__rating-score';
        score.setAttribute('data-ads-rating-score', '');
        score.setAttribute('aria-hidden', 'true');
        score.textContent = Number(match[1]).toFixed(1);
        // product-card.css hides the summary's first span (Yotpo's own stars), so sit beside the count.
        count.before(score);
      });
    }

    applyRows() {
      this.rows.forEach((row) => {
        if (row.extras.some((card) => card.contains(document.activeElement))) row.expanded = true;
        const expanded = !row.button || !this.mobile.matches || row.expanded;
        row.cards.forEach((card) => {
          card.hidden = !expanded && row.extras.includes(card);
        });
        if (!row.button) return;
        row.button.hidden = !this.mobile.matches || row.extras.length === 0;
        row.button.setAttribute('aria-expanded', String(expanded));
        const label = expanded ? row.button.dataset.lessLabel : row.button.dataset.moreLabel;
        const labelNode = row.button.querySelector('[data-gear-reveal-label]');
        if (labelNode && label) labelNode.textContent = label;
      });
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const reveal = target.closest('[data-gear-reveal]');
      if (reveal && this.contains(reveal)) {
        const row = this.rows.find((item) => item.button === reveal);
        if (!row || !this.mobile.matches) return;
        row.expanded = !row.expanded;
        reveal.focus({ preventScroll: true });
        this.applyRows();
        if (!row.expanded) {
          const top = reveal.getBoundingClientRect().top;
          const offset = this.headerHeight + this.navHeight + 16;
          if (top < offset) window.scrollBy({ top: top - offset, behavior: 'auto' });
        }
        this.track('duxbux:landing_navigation_selected', {
          ...this.rowData(row),
          destination_type: 'row_reveal',
          placement: 'product_row',
          action: row.expanded ? 'expand' : 'collapse'
        });
        return;
      }
      const play = target.closest('[data-gear-play]');
      if (play && this.contains(play)) {
        const record = this.videos.find((item) => item.button === play);
        if (record) this.toggleVideo(record);
        return;
      }
      this.trackLink(event);
    }

    scheduleLayout() {
      if (this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = 0;
        if (this.controller) this.measureLayout();
      });
    }

    measureLayout() {
      this.headerHeight = Math.ceil(this.header?.getBoundingClientRect().height || 0);
      this.navHeight = Math.ceil(this.nav?.getBoundingClientRect().height || 0);
      this.style.setProperty('--ads-header-height', `${this.headerHeight}px`);
      this.style.setProperty('--ads-nav-offset', `${this.navHeight}px`);
      const offset = Math.min(this.headerHeight + this.navHeight, Math.max(0, window.innerHeight - 1));
      this.headingObserver?.disconnect();
      this.exposureObserver?.disconnect();
      if ('IntersectionObserver' in window) {
        const bottomMargin = Math.min(Math.floor(window.innerHeight * 0.45), Math.max(0, window.innerHeight - offset - 1));
        this.headingObserver = new IntersectionObserver(() => this.updateCurrentJump(), {
          rootMargin: `-${offset}px 0px -${bottomMargin}px 0px`,
          threshold: 0
        });
        this.rows.forEach(({ heading }) => { if (heading) this.headingObserver.observe(heading); });
        this.exposureObserver = new IntersectionObserver(() => this.trackExposures(), {
          rootMargin: `-${offset}px 0px 0px 0px`, threshold: [0, 0.2]
        });
        this.exposureObserver.observe(this);
        this.rows.forEach(({ element }) => this.exposureObserver.observe(element));
      }
      this.updateCurrentJump();
      this.trackExposures();
    }

    updateCurrentJump() {
      const marker = this.headerHeight + this.navHeight + 64;
      let current = this.rows[0];
      this.rows.forEach((row) => {
        if (row.heading && row.heading.getBoundingClientRect().top <= marker) current = row;
      });
      this.jumps.forEach((link) => {
        if (current && link.dataset.gearJump === current.element.dataset.gearRow) {
          link.setAttribute('aria-current', 'true');
        } else link.removeAttribute('aria-current');
      });
    }

    prepareVideo(record) {
      const { video, button } = record;
      record.activated = false;
      record.failed = false;
      record.captioned = Boolean(video.querySelector('track[kind="captions"][src]'));
      video.controls = record.captioned;
      if (!record.captioned) {
        video.muted = true;
        video.defaultMuted = true;
        video.disablePictureInPicture = true;
        video.disableRemotePlayback = true;
        this.listen(video, 'volumechange', () => { if (!video.muted) video.muted = true; });
      }
      video.hidden = true;
      if (record.poster) record.poster.hidden = false;
      if (record.status) record.status.hidden = true;
      this.setVideoButton(record, 'play');
      button.hidden = false;
      this.listen(video, 'play', () => {
        document.querySelectorAll('ads-landing video').forEach((other) => {
          if (other !== video && !other.paused) other.pause();
        });
        this.setVideoButton(record, 'pause');
      });
      this.listen(video, 'playing', () => {
        button.removeAttribute('aria-busy');
        video.hidden = false;
        if (record.poster) record.poster.hidden = true;
        if (record.status) record.status.hidden = true;
        if (record.captioned && document.activeElement === button) video.focus({ preventScroll: true });
        button.hidden = record.captioned;
      });
      this.listen(video, 'pause', () => {
        record.request += 1;
        button.removeAttribute('aria-busy');
        button.hidden = false;
        this.setVideoButton(record, record.failed ? 'retry' : video.ended ? 'replay' : 'play');
      });
      this.listen(video, 'ended', () => {
        button.hidden = false;
        this.setVideoButton(record, 'replay');
      });
      this.listen(video, 'error', () => this.videoFailed(record));
      const sources = [...video.querySelectorAll('source[data-src]')];
      record.sourceErrors = new Set();
      sources.forEach((source) => this.listen(source, 'error', () => {
        if (!record.activated) return;
        record.sourceErrors.add(source);
        if (record.sourceErrors.size === sources.length) this.videoFailed(record);
      }));
    }

    setVideoButton(record, state) {
      const label = record.button.dataset[`${state}Label`] || record.button.dataset.playLabel;
      const text = record.button.querySelector('[data-gear-play-label]');
      if (label && text) text.textContent = label;
      if (label) record.button.setAttribute('aria-label', `${label}: ${record.video.getAttribute('aria-label') || ''}`);
      record.button.dataset.state = state;
      const icon = record.button.querySelector('svg path');
      const paths = { pause: 'M6 4h4v16H6zM14 4h4v16h-4z', retry: 'M12 5V1L7 6l5 5V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7z' };
      if (icon) icon.setAttribute('d', paths[state] || 'm8 4 12 8-12 8z');
    }

    // Silent field footage autoplays muted while at least half visible, like a
    // moving poster. Reduced-motion, data-saver and slow connections keep the
    // still poster and a play button; a shopper's own pause is never overridden.
    setupVideoAutoplay() {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const network = navigator.connection;
      const constrained = Boolean(network && (network.saveData || /(2g|3g)$/.test(network.effectiveType || '')));
      const candidates = this.videos.filter((record) => !record.captioned);
      if (reduceMotion || constrained || !('IntersectionObserver' in window) || !candidates.length) return;
      candidates.forEach((record) => { record.autoplay = true; });
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const record = candidates.find((item) => item.element === entry.target);
          if (!record) return;
          record.inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          this.syncAutoplay(record);
        });
      }, { threshold: [0, 0.5] });
      candidates.forEach((record) => this.videoObserver.observe(record.element));
    }

    syncAutoplay(record) {
      if (!record.autoplay || record.userPaused || record.failed) return;
      if (record.inView && !document.hidden) {
        if (record.video.paused) this.startVideo(record, true);
      } else if (!record.video.paused) record.video.pause();
    }

    toggleVideo(record) {
      const { video } = record;
      if (!video.paused && !video.ended) {
        record.userPaused = true;
        video.pause();
        this.track('duxbux:landing_proof_selected', { proof_id: `video_${Math.min(record.index + 1, 12)}`, action: 'pause' });
        return;
      }
      record.userPaused = false;
      this.startVideo(record, false);
      this.track('duxbux:landing_proof_selected', { proof_id: `video_${Math.min(record.index + 1, 12)}`, action: 'play' });
    }

    startVideo(record, auto) {
      const { video, button } = record;
      record.activated = true;
      const sources = [...video.querySelectorAll('source[data-src]')];
      if (!sources.length) { this.videoFailed(record); return; }
      const firstLoad = sources.some((source) => !source.hasAttribute('src'));
      record.sourceErrors?.clear();
      sources.forEach((source) => { if (!source.hasAttribute('src')) source.src = source.dataset.src; });
      if (firstLoad || record.failed) video.load();
      record.failed = false;
      if (record.status) record.status.hidden = true;
      if (!record.captioned) video.muted = true;
      if (video.ended) video.currentTime = 0;
      if (record.captioned) {
        video.hidden = false;
        if (record.poster) record.poster.hidden = true;
      }
      if (!auto) button.setAttribute('aria-busy', 'true');
      const request = ++record.request;
      const connection = this.controller;
      try {
        const playing = video.play();
        playing?.catch((error) => {
          if (this.controller !== connection || record.request !== request) return;
          // Low Power Mode and similar policies block muted autoplay; fall back
          // to the poster and play button instead of showing an error.
          if (auto && error?.name === 'NotAllowedError') {
            record.autoplay = false;
            button.removeAttribute('aria-busy');
            this.setVideoButton(record, 'play');
            return;
          }
          this.videoFailed(record);
        });
      } catch (_) { this.videoFailed(record); }
    }

    videoFailed(record) {
      if (!this.controller || !record.activated) return;
      const shouldFocus = record.video === document.activeElement || record.button === document.activeElement;
      record.request += 1;
      record.video.pause();
      record.video.hidden = true;
      record.failed = true;
      if (record.poster) record.poster.hidden = false;
      if (record.status) record.status.hidden = false;
      record.button.hidden = false;
      record.button.removeAttribute('aria-busy');
      this.setVideoButton(record, 'retry');
      if (shouldFocus) record.button.focus({ preventScroll: true });
    }

    isLiveStorefront() {
      const shopify = window.Shopify;
      if (this.dataset.editor === 'true' || shopify?.designMode || shopify?.previewMode || shopify?.theme?.role !== 'main') return false;
      const query = new URLSearchParams(window.location.search);
      if (['preview_theme_id', 'preview_key', '_fd', 'view', 'ads_variant'].some((key) => query.has(key))) return false;
      if (/\.shopifypreview\.com$/.test(window.location.hostname)) return false;
      return !document.querySelector('#PBarNextFrame, #preview-bar-iframe, #shopify-preview-bar, [data-shopify-preview-bar]');
    }

    setupPrivacy() {
      if (!this.isLiveStorefront()) return;
      this.listen(document, 'visitorConsentCollected', () => this.trackExposures());
      const connection = this.controller;
      const ready = () => {
        if (this.controller !== connection) return;
        if (window.Shopify?.customerPrivacy) { this.trackExposures(); return; }
        if (typeof window.Shopify?.loadFeatures !== 'function' || this.privacyRequested) return;
        this.privacyRequested = true;
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (!error && this.controller === connection) this.trackExposures();
          });
        } catch (_) {}
      };
      ready();
      if (document.readyState !== 'complete') this.listen(window, 'load', ready, { once: true });
    }

    canTrack() {
      if (!this.controller || document.hidden || !this.isLiveStorefront()) return false;
      try {
        return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true
          && typeof window.Shopify?.analytics?.publish === 'function';
      } catch (_) { return false; }
    }

    publish(name, detail = {}) {
      if (!this.canTrack()) return false;
      try {
        const sent = window.Shopify.analytics.publish(name, {
          page_id: 'ads_landing',
          page_version: token(this.dataset.version, 'gear-v1'),
          variant: ['control', 'all-six'].includes(this.dataset.variant) ? this.dataset.variant : 'control',
          experiment_id: 'none', experiment_state: 'disabled', traffic_context: 'storefront',
          ...detail
        });
        sent?.catch?.(() => {});
        return true;
      } catch (_) { return false; }
    }

    visibleRatio(element) {
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return 0;
      const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, this.headerHeight + this.navHeight));
      const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      return height * width / (rect.width * rect.height);
    }

    trackExposures() {
      if (!this.canTrack()) return;
      if (!this.viewed && this.visibleRatio(this) > 0) this.viewed = this.publish('duxbux:landing_viewed');
      this.rows.forEach((row) => {
        if (this.seenRows.has(row.element.dataset.gearRow) || this.visibleRatio(row.element) < 0.2) return;
        if (this.publish('duxbux:landing_row_viewed', this.rowData(row))) this.seenRows.add(row.element.dataset.gearRow);
      });
    }

    rowData(row) {
      const key = row.element.dataset.gearRowKey;
      return {
        row_id: ROW_KEYS.includes(key) ? key : `row_${Math.min(row.index + 1, 4)}`,
        row_position: Math.min(row.index + 1, 4), product_count: Math.min(row.cards.length, 6)
      };
    }

    track(name, detail) {
      this.trackExposures();
      this.publish(name, detail);
    }

    trackLink(event) {
      if (event.defaultPrevented || !this.canTrack()) return;
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest('a[href]');
      if (!link || !this.contains(link)) return;
      const row = this.rows.find((item) => item.element.contains(link));
      const card = link.closest('[data-gear-card]');
      let url;
      try { url = new URL(link.href, window.location.origin); } catch (_) { return; }
      const product = url.pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([a-zA-Z0-9-]{1,100})\/?$/);
      if (card && row && product && url.origin === window.location.origin) {
        const productCard = card.querySelector('[data-product-id]');
        const id = productCard?.dataset.productId;
        this.track('duxbux:landing_product_selected', {
          ...this.rowData(row), product_id: /^\d{1,20}$/.test(id || '') ? id : 'unknown',
          product_handle: product[1], product_position: Math.min(row.cards.indexOf(card) + 1, 6),
          product_brand: ['dux', 'bux', 'shared'].includes(productCard?.dataset.productBrand) ? productCard.dataset.productBrand : 'shared'
        });
        return;
      }
      const proof = link.closest('.ads-landing__story, .ads-landing__review');
      if (proof) {
        const proofs = [...this.querySelectorAll('.ads-landing__story, .ads-landing__review')];
        this.track('duxbux:landing_proof_selected', {
          proof_id: `proof_${Math.min(proofs.indexOf(proof) + 1, 12)}`, action: 'link'
        });
        return;
      }
      const safePath = /^\/(?:collections|pages|policies)\/[a-zA-Z0-9-]{1,100}\/?$/.test(url.pathname)
        && url.origin === window.location.origin ? url.pathname : '';
      if (link.hasAttribute('data-gear-jump')) {
        const destination = this.rows.find((item) => item.element.dataset.gearRow === link.dataset.gearJump);
        this.track('duxbux:landing_navigation_selected', {
          destination_type: 'row_jump', placement: 'jump_nav',
          ...(destination ? this.rowData(destination) : {})
        });
      } else if (safePath) {
        this.track('duxbux:landing_navigation_selected', {
          destination_type: safePath.split('/')[1], destination_path: safePath,
          placement: row ? 'product_row' : link.closest('.ads-landing__help') ? 'help' : 'closing'
        });
      }
    }
  }

  customElements.define('ads-landing', AdsLanding);
})();
